<template>
  <div>
    <WdsRadioGroup
      v-model:checked="ukCompanyType"
      label="Does IR35 apply to your company?"
      name="uk_company_type"
    >
      <WdsRadio value="non_small_private" data-test="ir35-company-type-non-small-private">
        Yes, IR35 applies to us
        <br />
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsText class="tw-mt-2" secondary>
          IR35 applies to medium and large-sized private sector clients or any public sector clients
        </WdsText>
      </WdsRadio>
      <WdsRadio class="tw-mt-2" value="small_private" data-test="ir35-company-type-small-private">
        No, IR35 does not apply to us, as we're a small company
        <br />
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsText class="tw-mt-2" secondary>
          Your company is NOT considered a
          <em>small company</em>
          if you
          <u>or your corporate group</u>
          meet at least
          <strong>two</strong>
          of the following conditions:
          <ul class="tw-mt-2 tw-mb-0">
            <li>You have an annual turnover of more than £10.2 million</li>
            <li>You have a balance sheet total of more than £5.1 million</li>
            <li>You have more than 50 employees</li>
          </ul>
        </WdsText>
      </WdsRadio>
    </WdsRadioGroup>

    <WdsAlert v-if="shouldNotUpdate" class="tw-mt-4" icon warning>
      Your company has active hires
      <template #description>
        Changing whether IR35 applies to your company while having active hires can lead to invalid
        contracts.
        <small class="tw-mt-2">
          Please reach out to Worksome if you need any help or have questions.
        </small>
      </template>
    </WdsAlert>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-2"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>
        {{ errorMessage }}
      </template>
    </WdsAlert>
    <WdsAlert v-else-if="showSuccess" class="tw-mt-2" closable success @close="showSuccess = false">
      {{ $t('Thanks, your settings has been updated') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton :disabled="loading" primary data-test="ir35-company-type-save-button" @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>Save</span>
          <WdsSpinner v-show="loading" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
} from '@worksome/wds-components'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { FormValidation } from '@/utils/formValidation'

export default {
  components: {
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsRadio,
    WdsRadioGroup,
    WdsSpinner,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const errors = ref([])
    const internalComplianceData = reactive(props.complianceData)
    const loading = ref(false)
    const showError = ref(false)
    const showSuccess = ref(false)

    return {
      errors,
      internalComplianceData,
      loading,
      showError,
      showSuccess,
    }
  },
  computed: {
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
    shouldNotUpdate() {
      return this.internalComplianceData.shouldNotUpdate ?? false
    },
    ukCompanyType: {
      get() {
        return this.internalComplianceData.ukCompanyType ?? 'unknown'
      },
      set(ukCompanyType) {
        this.internalComplianceData.ukCompanyType = ukCompanyType
        this.$emit('update:complianceData', this.internalComplianceData)
      },
    },
  },
  methods: {
    async onSave() {
      this.loading = true
      this.showError = false
      this.showSuccess = false

      const data = JSON.stringify({
        uk_company_type: this.ukCompanyType,
      })

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: 'IR35_COMPANY_SETTINGS',
              data,
            },
          },
        })

        this.showSuccess = true
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
